import { Box, Flex, Text } from '@chakra-ui/layout';
import { Icon } from '@chakra-ui/react';
import { IconCrossLarge } from 'central-icons/IconCrossLarge';
import { IconPlusLarge } from 'central-icons/IconPlusLarge';

import type { SelectionCategories } from '@revelio/iso-utility';
import { Button } from '@revelio/ui';

import {
  GEOGRAPHY_GRANULARITY_FILTERS,
  ROLE_GRANULARITY_FILTERS,
} from '../../constants/filter-constants';
import {
  type Filter,
  FilterType,
  type TreeSelection,
} from '../../types/filters';
import styles from './SegmentForm.module.css';

interface SegmentFormProps {
  segmentations: Record<string, Filter> | undefined;
  onEditSegment: (segmentKey: string) => () => void;
  onRemoveSegment: (
    segmentKey: string
  ) => (e: React.MouseEvent<SVGElement, MouseEvent>) => void;
  onNewSegment: () => void;
}

const getSegmentGroup = (levelId: string): string => {
  if (ROLE_GRANULARITY_FILTERS.includes(levelId as SelectionCategories)) {
    return 'Role_Granularity';
  }

  if (GEOGRAPHY_GRANULARITY_FILTERS.includes(levelId as SelectionCategories)) {
    return 'Geography_Granularity';
  }

  return levelId;
};

const getDisplayNames = (
  selections: TreeSelection[] | Array<{ id: string; name: string }>
): string => {
  const names = selections.map((s) => s.name).filter(Boolean);

  return names.length > 2
    ? [
        ...names.slice(0, 2),
        `and ${names.length - 2} other${names.length - 2 > 1 ? 's' : ''}`,
      ].join(', ')
    : names.join(', ');
};

const getSegmentLevelId = (filter: Filter): string | null => {
  if (filter.type === FilterType.TREE && filter.selections.length > 0) {
    return filter.selections[0]?.levelId ?? null;
  }
  if (filter.type === FilterType.LIST && filter.selections.length > 0) {
    // For list filters, use the selectionListId as the levelId
    return filter.selectionListId ?? null;
  }
  return null;
};

export const SegmentForm = ({
  segmentations,
  onEditSegment,
  onRemoveSegment,
  onNewSegment,
}: SegmentFormProps) => {
  const hasSegments = segmentations && Object.keys(segmentations).length > 0;

  // Group segments by their levelId
  const groupedSegments = segmentations
    ? Object.entries(segmentations).reduce<
        Record<string, Array<[string, Filter]>>
      >((groups, [key, filter]) => {
        const levelId = getSegmentLevelId(filter);
        if (!levelId) return groups;

        const group = getSegmentGroup(levelId);
        if (!groups[group]) {
          groups[group] = [];
        }
        groups[group].push([key, filter]);
        return groups;
      }, {})
    : {};

  return (
    <>
      {hasSegments && (
        <Box marginLeft={2} paddingX={1.5}>
          {Object.entries(groupedSegments).map(([groupKey, segments]) => {
            const firstSegment = segments[0];
            if (!firstSegment) return null;

            const [segmentKey, filter] = firstSegment;
            let displayNames = '';

            if (
              filter.type === FilterType.TREE ||
              filter.type === FilterType.LIST
            ) {
              displayNames = getDisplayNames(filter.selections);
            }

            return (
              <Flex key={groupKey} marginBottom={1.5} alignItems="center">
                <Text fontSize={14}>
                  {GEOGRAPHY_GRANULARITY_FILTERS.includes(
                    groupKey as SelectionCategories
                  )
                    ? 'in'
                    : 'for'}
                </Text>
                <Flex
                  onClick={onEditSegment(segmentKey)}
                  alignItems="center"
                  cursor="pointer"
                  backgroundColor="lightBlue.100"
                  borderRadius="4px"
                  px={1}
                  ml={1}
                >
                  <Text fontSize={14} fontWeight={400} color="#079CCF">
                    {displayNames}
                  </Text>
                  <Icon
                    as={IconCrossLarge}
                    cursor="pointer"
                    onClick={onRemoveSegment(segmentKey)}
                    color="lightBlue.700"
                    ml={1}
                    data-testid={`remove-segment-${segmentKey}`}
                  />
                </Flex>
              </Flex>
            );
          })}
        </Box>
      )}
      <Button
        onClick={onNewSegment}
        size="sm"
        className={styles.newSegmentButton}
        style={{
          marginBottom: '0.625rem',
          marginLeft: hasSegments ? '0.75rem' : 0,
        }}
      >
        <Icon as={IconPlusLarge} boxSize={3} marginTop="2px" marginRight={1} />
        <Text as="span" color="lightBlue.350">
          segment by employee type
        </Text>
      </Button>
    </>
  );
};
